
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios'; 

// Context & API
import { useAuth } from '../../../context/AuthContext';
import { saveOrUpdateUserProfileApi, fetchUserJogetProfileApi, fetchUserJogetProfileUUDIApi } from '../../../services/api';
import { fetchLookupDataApi } from '../../../services/lookupApi';

import MetaTags from '../../../components/MetaTags';

// Components
import ProfileWizard from '../../../components/dashboard/profile/ProfileWizard';
import StepPersonalInfo from '../../../components/dashboard/profile/StepPersonalInfo';
import StepPhysicalStats from '../../../components/dashboard/profile/StepPhysicalStats';
import StepAddress from '../../../components/dashboard/profile/StepAddress';
import StepSocialLinks from '../../../components/dashboard/profile/StepSocialLinks';
import StepExperience from '../../../components/dashboard/profile/StepExperience';
import StepEducation from '../../../components/dashboard/profile/StepEducation';

// Utils & Schema
import { profileSchema } from '../../../validation/profileSchema';
import { apiToDateObject, dateObjectToApiFormat } from '../../../utils/dateHelpers';
import { convertCmToFeetInches, convertFeetInchesToCm, convertCmToMeters } from '../../../utils/heightHelpers';
import { transformToSelectOptions } from '../../../utils/selectUtils';
import Swal from 'sweetalert2';

const WIZARD_STEPS = [
    { id: 0, title: 'Personal Info', fields: ['fullName', 'email', 'mobileNumber', 'dateOfBirth', 'gender', 'specialCategory', 'countryOfResidence', 'preferredCurrency', 'talents', 'alternateMobileNumber', 'educationLevel', 'nationality', 'aboutYourself', 'panNumber', 'aadharNumber'] },
    { id: 1, title: 'Physical Stats', fields: ['weight', 'heightFeet', 'heightInches', 'eyeColour', 'skinType', 'bodyType', 'hairType', 'hairColor', 'esportsType', 'drivingType', 'peopleAppearanceList', 'sportsType', 'esportsType'] },
    { id: 2, title: 'Address', fields: ['pinCode', 'addressLine1', 'addressLine2', 'state', 'district'] },
    { id: 3, title: 'Social Links', fields: ['facebookProfileUrl', 'instagramProfileUrl', 'linkedinProfileUrl', 'twitterProfileUrl', 'youtubeUrl', 'whatsappNo', 'imdbUrl'] },
    { id: 4, title: 'Experience', fields: ['experiences'] },
    { id: 5, title: 'Training & Education', fields: ['educations'] } 
];

const MyProfilePage = () => {
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [apiMessage, setApiMessage] = useState({ type: '', text: '' });
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [currentJogetRecordId, setCurrentJogetRecordId] = useState(null);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [isWhatsappSame, setIsWhatsappSame] = useState(false);
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const isViewMode = query.get("mode") === "view";
    const isKnowMore = query.get("knowMore") === "true";

    const profileId = params.id;
    const hasAdminToken = !!localStorage.getItem('adminToken');
    const stepQuery = query.get("step");

    useEffect(() => {
        if (stepQuery !== null) {
            const parsedStep = parseInt(stepQuery, 10);
            if (!isNaN(parsedStep) && parsedStep >= 0 && parsedStep < WIZARD_STEPS.length) {
                setCurrentStep(parsedStep);
            }
        }
    }, [stepQuery]);

    const [lookupOptions, setLookupOptions] = useState({
        bodyType: [], gender: [], specialCategory: [], nationality: [], countryOfResidence: [], preferredCurrency: [],
        eyeColour: [], skinType: [], hairType: [], hairColor: [], talent: [], addressType: [], educationLevel: [], sportsType: [], esportsType: [], drivingType: [],
        peopleAppearanceList: [], projectType: [], typeOfGenres: []
    });

    const { control, handleSubmit, reset, trigger, setValue, watch, clearErrors, formState: { errors } } = useForm({
        resolver: yupResolver(profileSchema),
        mode: 'onChange',
        defaultValues: { 
            dateOfBirth: null, addressType: 'primary', heightFeet: null, heightInches: null, talents: [], imdbUrl: '' ,
            experiences: [], educations: [] 
        } 
    });

    // --- State & Watchers ---
    const isSports = selectedSkills.some(v => v.value === "sports");
    const isEsports = selectedSkills.some(v => v.value === "esports");
    const mergedOptions = [
        ...(Array.isArray(lookupOptions.sportsType) ? lookupOptions.sportsType : []),
        ...(Array.isArray(lookupOptions.esportsType) ? lookupOptions.esportsType : [])
    ].filter((v, i, arr) => arr.findIndex(t => t.value === v.value) === i);
    
    const [firstFilled, setFirstFilled] = useState(null);
    const pan = watch("panNumber");
    const aadhar = watch("aadharNumber");
    const watchedMobile = watch('mobileNumber');
    const watchedHeightFeet = watch('heightFeet');
    const watchedHeightInches = watch('heightInches');
    const watchedPincode = watch('pinCode');

    useEffect(() => { if (pan || aadhar) clearErrors(["panNumber", "aadharNumber"]); }, [pan, aadhar, clearErrors]);
    useEffect(() => {
        if (!firstFilled) {
            if (pan) setFirstFilled("pan");
            else if (aadhar) setFirstFilled("aadhar");
        }
    }, [pan, aadhar, firstFilled]);
    useEffect(() => { if (!pan && !aadhar) setFirstFilled(null); }, [pan, aadhar]);

    const isPanRequired = (!pan && !aadhar) || firstFilled === "pan";
    const isAadharRequired = (!pan && !aadhar) || firstFilled === "aadhar";

    const feetOptions = Array.from({ length: 8 }, (_, i) => ({ value: i + 1, label: `${i + 1} Ft` }));
    const inchOptions = Array.from({ length: 12 }, (_, i) => ({ value: i, label: `${i} In` }));

    const displayCalculatedHeight = useMemo(() => {
        if (watchedHeightFeet !== null && watchedHeightInches !== null) {
            const totalCm = convertFeetInchesToCm(watchedHeightFeet, watchedHeightInches);
            const totalMeters = convertCmToMeters(totalCm);
            return totalCm ? `${watchedHeightFeet}' ${watchedHeightInches}" (${totalMeters} m)` : '';
        }
        return '';
    }, [watchedHeightFeet, watchedHeightInches]);

    useEffect(() => { if (isWhatsappSame) setValue('whatsappNo', watchedMobile || ''); }, [isWhatsappSame, watchedMobile, setValue]);

    const handleWhatsappCheck = (e) => {
        const checked = e.target.checked;
        setIsWhatsappSame(checked);
        if (checked) setValue('whatsappNo', watchedMobile || '');
        else setValue('whatsappNo', '');
    };

    const handlePincodeLookup = useCallback(async (pincode) => {
        if (!pincode || pincode.length !== 6) return;
        try {
            let response;
            try {
                response = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);
                if (!response.data?.[0] || response.data?.[0]?.Status !== "Success") {
                    response = await axios.get(`http://www.postalpincode.in/api/pincode/${pincode}`);
                    response.data = [{
                        Status: response.data.Status,
                        PostOffice: response.data.PostOffice
                    }];
                }
            } catch {
                response = await axios.get(`http://www.postalpincode.in/api/pincode/${pincode}`);
                response.data = [{
                    Status: response.data.Status,
                    PostOffice: response.data.PostOffice
                }];
            }
            if (response.data?.[0]?.Status === 'Success') {
                const { State, District } = response.data[0].PostOffice[0];
                setValue('state', State, { shouldValidate: true });
                setValue('district', District, { shouldValidate: true });
            } else {
                setValue('state', ''); setValue('district', '');
                setApiMessage({ type: 'warning', text: `Pincode not found.` });
            }
        } catch (error) { 
            setApiMessage({ type: 'danger', text: 'Failed to fetch pincode details.' });
        }
    }, [setValue]);

    useEffect(() => { if (watchedPincode && watchedPincode.length === 6) handlePincodeLookup(watchedPincode); }, [watchedPincode, handlePincodeLookup]);

    const loadData = useCallback(async () => {
        if (!user?.id && !hasAdminToken) return;
        setPageLoading(true);
        try {
            const lookupGroups = {
                bodyType: 'bodyType', gender: 'gender', specialCategory: 'specialCategory', nationality: 'nationality',
                countryOfResidence: 'countryOfResidence', preferredCurrency: 'preferredCurrency', eyeColour: 'eyeColour',
                skinType: 'skinColor', hairType: 'hairType', hairColor: 'hairColor', talent: 'jobTalent', addressType: 'addressType', 
                educationLevel: 'eduQualification', esportsType: 'esportsType', sportsType: 'sportsType', drivingType: 'drivingType', peopleAppearanceList: 'peopleAppearanceList', projectType: 'projectType', typeOfGenres: 'typeOfGenres' 
            };
            
            const promises = Object.entries(lookupGroups).map(([key, group]) => fetchLookupDataApi(group));
            const results = await Promise.all(promises);
            
            let finalLookups = {};
            Object.keys(lookupGroups).forEach((key, i) => { finalLookups[key] = transformToSelectOptions(results[i].data.data); });
            setLookupOptions(finalLookups);
            
            let jogetData = null;

            if (hasAdminToken && profileId && profileId !== "add") {
                const response = await fetchUserJogetProfileApi(profileId);
                jogetData = response?.data?.jogetData;
                setProfileImagePreview(response?.data?.profileImageUrl || jogetData?.profileImage || null);
            } else if (!hasAdminToken && profileId && !isViewMode) {
                const response = await fetchUserJogetProfileUUDIApi(profileId);
                jogetData = response?.data?.jogetData;
                setProfileImagePreview(response?.data?.profileImageUrl || jogetData?.profileImage || null);
            } else if (!profileId && !hasAdminToken && user?.id) {
                const response = await fetchUserJogetProfileApi(user.id);
                jogetData = response?.data?.jogetData;
            }

            if (jogetData) {
                setCurrentJogetRecordId(jogetData.pk_id);

                const talentsForSelect = (jogetData.talent || '').split(',')
                    .map(val => val.trim()).map(val => finalLookups.talent.find(opt => String(opt.value) === String(val))).filter(Boolean);

                let heightFeet = null, heightInches = null;
                if (jogetData.height) {
                    const h = convertCmToFeetInches(parseFloat(jogetData.height));
                    heightFeet = h.feet; heightInches = h.inches;
                }

                reset({
                  ...jogetData,
                  addressType: jogetData?.addressType || 'primary',
                  dateOfBirth: apiToDateObject(jogetData.dateOfBirth),
                  talents: talentsForSelect,
                  heightFeet,
                  heightInches,
                  educationLevel: finalLookups.educationLevel.find(opt => opt.value === jogetData.educationLevel) || null,
                  gender: finalLookups.gender.find(opt => opt.value === jogetData.gender) || null,
                  nationality: finalLookups.nationality.find(opt => opt.value === jogetData.nationality) || null,
                  countryOfResidence: finalLookups.countryOfResidence.find(opt => opt.value === jogetData.countryOfResidence) || null,
                  preferredCurrency: finalLookups.preferredCurrency.find(opt => opt.value === jogetData.preferredCurrency) || null,
                  eyeColour: finalLookups.eyeColour.find(opt => opt.value === jogetData.eyeColour) || null,
                  skinType: finalLookups.skinType.find(opt => opt.value === jogetData.skinType) || null,
                  bodyType: finalLookups.bodyType.find(opt => opt.value === jogetData.bodyType) || null,
                  hairType: finalLookups.hairType.find(opt => opt.value === jogetData.hairType) || null,
                  hairColor: finalLookups.hairColor.find(opt => opt.value === jogetData.hairColor) || null,
                  drivingType: finalLookups.drivingType.find(opt => opt.value === jogetData.drivingType) || null,
                  peopleAppearanceList: finalLookups.peopleAppearanceList.find(opt => opt.value === jogetData.peopleAppearanceList) || null,
                  esportsType: Array.isArray(jogetData.esportsType)
                    ? jogetData.esportsType.map(val => finalLookups.esportsType.find(opt => opt.value === val)).filter(Boolean)
                    : [],
                  sportsType: Array.isArray(jogetData.sportsType)
                    ? jogetData.sportsType.map(val => finalLookups.sportsType.find(opt => opt.value === val)).filter(Boolean)
                    : [],
                  experiences: Array.isArray(jogetData.experiences) ? jogetData.experiences.map(exp => {
                    const genresForSelect = (exp.typeOfGenres || '').split(',')
                        .map(val => val.trim())
                        .map(val => finalLookups.typeOfGenres.find(opt => String(opt.value) === String(val)))
                        .filter(Boolean);
                    const talentsForSelect = (exp.talent || '').split(',')
                          .map(val => val.trim())
                          .map(val => finalLookups.talent.find(opt => String(opt.value) === String(val)))
                          .filter(Boolean);

                    return {
                        ...exp,
                        projectType: finalLookups.projectType.find(opt => opt.value === exp.projectType) || exp.projectType,
                        typeOfGenres: genresForSelect, talent: talentsForSelect 
                    };
                  }) : [],
                  educations: jogetData.educations || [], 
                  imdbUrl: jogetData.imdbUrl || '',
                });

                if(jogetData.whatsappNo && jogetData.whatsappNo === jogetData.mobileNumber) setIsWhatsappSame(true);
                if (jogetData.profileImage) setProfileImagePreview(jogetData.profileImage || jogetData.profileImageUrl);
            }
        } catch (error) {
            setApiMessage({ type: 'danger', text: 'Failed to load profile.' }); 
        } finally { setPageLoading(false); }
    }, [user, reset, hasAdminToken, profileId]);

    useEffect(() => { loadData(); }, [loadData]);

    // ✅ AUTO-SAVE ON NEXT BUTTON CLICK
    const handleNext = async () => {
        console.log("STEP:", currentStep);
        const isValid = await trigger(WIZARD_STEPS[currentStep].fields);

        if (!isValid) {
            setApiMessage({ type: 'danger', text: 'Please fill all required fields in the current step.' }); 
            window.scrollTo(0,0);
            return;
        }

        // Save data to backend database immediately before changing state
        await handleSubmit(onSubmit)();

        if (currentStep < WIZARD_STEPS.length - 1) {
            setCurrentStep(c => c + 1);
            window.scrollTo(0, 0);
        }
    };

    // ✅ AUTO-SAVE ON BACK BUTTON CLICK
    const handleBack = async () => {
        if (currentStep > 0) {
            const fieldsToValidate = WIZARD_STEPS[currentStep]?.fields || [];
            const isValid = await trigger(fieldsToValidate);
            
            // Save progress if current fields are valid
            if (isValid) {
                await handleSubmit(onSubmit)();
            }
            
            setApiMessage({ type: '', text: '' });
            setCurrentStep(c => c - 1);
            window.scrollTo(0, 0);
        }
    };

    // ✅ AUTO-SAVE ON HEADER STEP TABS CLICK
    const handleStepClick = async (targetStep) => {
        if (targetStep === currentStep) return;

        const fieldsToValidate = WIZARD_STEPS[currentStep]?.fields || [];
        const isValid = await trigger(fieldsToValidate);

        if (isValid) {
            // Save current step data before navigating to target tab
            await handleSubmit(onSubmit)();
            setApiMessage({ type: '', text: '' });
            setCurrentStep(targetStep);
            window.scrollTo(0, 0);
        } else {
            // Allow going back to edit, but block going forward if invalid
            if (targetStep < currentStep) {
                setApiMessage({ type: '', text: '' });
                setCurrentStep(targetStep);
                window.scrollTo(0, 0);
            } else {
                setApiMessage({ type: 'danger', text: 'Please fill all required fields in the current step first.' });
                window.scrollTo(0, 0);
            }
        }
    };
    
    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const formData = new FormData();
            if (hasAdminToken && profileId !== "add") {
                formData.append('user_id', profileId);
            } else {
                formData.append('user_id', user.id);
            }
            if (currentJogetRecordId) formData.append('id', currentJogetRecordId);

            const heightInCm = convertFeetInchesToCm(data.heightFeet, data.heightInches);
            formData.append('height', heightInCm ? heightInCm.toFixed(2) : '');
            formData.append('talent', (data.talents || []).map(t => t.value).join(','));
            
            if (data.experiences && data.experiences.length > 0) {
                const experiencesFormatted = data.experiences.map((exp, index) => {
                    if (exp.attachmentFile instanceof File) {
                        formData.append(`experience_file_${index}`, exp.attachmentFile);
                    }
                    const genresString = Array.isArray(exp.typeOfGenres)
                        ? exp.typeOfGenres.map(g => g.value).join(',')
                        : '';
                    const talentsString = Array.isArray(exp.talent)
                        ? exp.talent.map(t => t.value).join(',')
                        : '';

                    const { attachmentFile, ...rest } = exp;
                    return {
                        ...rest,
                        typeOfGenres: genresString,
                        talent: talentsString
                    };
                });
                formData.append('experiences', JSON.stringify(experiencesFormatted));
            } else {
                formData.append('experiences', JSON.stringify([]));
            }

            if (data.educations && data.educations.length > 0) {
                const educationsFormatted = data.educations.map((edu, index) => {
                    if (edu.certificateFile instanceof File) {
                        formData.append(`education_file_${index}`, edu.certificateFile);
                    }
                    const { certificateFile, ...rest } = edu;
                    return rest;
                });
                formData.append('educations', JSON.stringify(educationsFormatted));
            } else {
                formData.append('educations', JSON.stringify([]));
            }

            // ✅ Added 'educations' to this exclude list to prevent raw object double-appending serialization corruption
            const exclude = [
                'heightFeet', 
                'heightInches', 
                'talents', 
                'height', 
                'talent', 
                'profileImageFile', 
                'user_id', 
                'id', 
                'pk_id', 
                'experiences', 
                'educations' // 👈 Fixed education blank bug!
            ];

            Object.keys(data).forEach(key => {
                if (!exclude.includes(key) && data[key] !== null && data[key] !== undefined) {
                    let value = data[key];
                    if (Array.isArray(value)) {
                        value = value.map(v => (typeof v === "object" ? v.value : v)).join(',');
                    } else if (typeof value === "object") {
                        value = value?.value ?? value;
                    } 
                    if (key === 'dateOfBirth' && value instanceof Date) {
                        value = dateObjectToApiFormat(value);
                    }
                    formData.append(key, value);
                }
            });

            if (data.profileImageFile instanceof File) formData.append('profileImageFile', data.profileImageFile);

            await saveOrUpdateUserProfileApi(formData);
            
            // Dynamic alert messages
            setApiMessage({ type: 'success', text: 'Progress saved successfully!' });
            
            // Redirect only when completing the last step (Step 5)
            if (hasAdminToken && currentStep === 5) {
                setTimeout(() => {
                    navigate('/admin/users');
                }, 1000);
            }
        } catch (error) { 
            setApiMessage({ type: 'danger', text: 'Failed to auto-save step data.' }); 
        } finally { setLoading(false); }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const maxSize = 1 * 1024 * 1024;
        if (!file.type.startsWith('image/')) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid File',
                text: 'Only image files are allowed.',
                confirmButtonColor: '#c30000'
            });
            e.target.value = '';
            return;
        }
        if (file.size > maxSize) {
            Swal.fire({
                icon: 'warning',
                title: 'Max 1MB Only',
                text: 'Upload image below 1MB',
                confirmButtonColor: '#c30000',
                customClass: {
                    popup: 'custom-swal-popup',
                    title: 'custom-swal-title',
                    htmlContainer: 'custom-swal-text',
                    confirmButton: 'custom-swal-btn',
                    icon: 'custom-swal-icon'
                }
            });
            e.target.value = '';
            return;
        }
        setValue('profileImageFile', file);
        setProfileImagePreview(URL.createObjectURL(file));
    };

    if (pageLoading) return <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>;

    return (
        <div className="my-profile-page-wrapper">
            <MetaTags title="My Profile" />
            <h5 className="text-uppercase text-white fw-bold mb-4"><span className="text-danger border-start border-4 border-danger ps-3">My Profile</span></h5>

            <ProfileWizard currentStep={currentStep} steps={WIZARD_STEPS} onStepClick={handleStepClick} />

            {apiMessage.text && <Alert variant={apiMessage.type} dismissible onClose={() => setApiMessage({type:'', text:''})}>{apiMessage.text}</Alert>}

            <div className="custom-card-box">
                <Form noValidate>
                    {currentStep === 0 && <StepPersonalInfo control={control} errors={errors} lookupOptions={lookupOptions} profileImagePreview={profileImagePreview} handleImageChange={handleImageChange} isWhatsappSame={isWhatsappSame} handleWhatsappCheck={handleWhatsappCheck} isPanRequired={isPanRequired} isAadharRequired={isAadharRequired} />}
                    {currentStep === 1 && <StepPhysicalStats control={control} errors={errors} lookupOptions={lookupOptions} feetOptions={feetOptions} inchOptions={inchOptions} displayCalculatedHeight={displayCalculatedHeight} selectedSkills={selectedSkills} setSelectedSkills={setSelectedSkills} isSports={isSports} isEsports={isEsports} mergedOptions={mergedOptions} />}
                    {currentStep === 2 && <StepAddress control={control} errors={errors} />}
                    {currentStep === 3 && <StepSocialLinks control={control} errors={errors} isWhatsappSame={isWhatsappSame} />}
                    {currentStep === 4 && <StepExperience control={control} errors={errors} lookupOptions={lookupOptions} />}
                    {currentStep === 5 && <StepEducation control={control} errors={errors} />}
                </Form>
            </div>

            <div className="d-flex justify-content-between mt-4">
                <Button variant="outline-secondary" onClick={handleBack} disabled={currentStep === 0 || loading} size="lg" className="px-4 border-secondary text-light"><FaChevronLeft className="me-2" /> Back</Button>
                {!(isViewMode || isKnowMore && currentStep === 5) && (
                    <Button variant="danger" onClick={handleNext} disabled={loading} size="lg" className="px-5 fw-bold shadow-lg" style={{ minWidth: '180px' }}>
                        {loading ? <Spinner animation="border" size="sm" className="text-white"/> : (currentStep === 5 ? 'SAVE PROFILE' : <>Next <FaChevronRight className="ms-2" /></>)}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default MyProfilePage;